import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import { DealVendorNoteModal } from "~/components/common/deals/show/vendor_card/DealVendorNoteModal";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import { useDealVendorNotes } from "~/hooks/vendors/deals/useDealVendorNotes";
import type { Deal } from "~/models/Deal";
import type { DealVendorNote } from "~/models/DealVendorNote";

type DealVendorNoteContextValue = {
	dealVendorNote: DealVendorNote | null;
	handleOpen: () => void;
	isReady: boolean;
};

const DealVendorNoteContext = createContext<DealVendorNoteContextValue | null>(
	null
);

export const useDealVendorNoteContext = (): DealVendorNoteContextValue =>
	useContext(DealVendorNoteContext)!;

type Props = {
	children: ReactNode;
	deal: Deal;
	handleMutateDeal: () => Promise<void>;
};

export function DealVendorNoteProvider({
	children,
	deal,
	handleMutateDeal
}: Props) {
	const {
		data: dealVendorNote,
		isLoading,
		mutate
	} = useDealVendorNotes().show(deal.id, deal.dealVendorNoteId);
	const { data: dealVendorNoteTemplate } = useDealVendorNotes().template(
		deal.id
	);
	const { isOpen, handleClose, handleOpen } = useOpenHandler();
	const handleMutate = async (): Promise<void> => {
		if (dealVendorNote === null) {
			await handleMutateDeal();
		} else {
			await mutate();
		}
	};
	const isReady = !isLoading && dealVendorNoteTemplate !== null;

	return (
		<DealVendorNoteContext.Provider
			value={{ dealVendorNote, handleOpen, isReady }}
		>
			{children}
			{isReady ? (
				<DealVendorNoteModal
					deal={deal}
					dealVendorNote={dealVendorNote}
					dealVendorNoteTemplate={dealVendorNoteTemplate}
					handleClose={handleClose}
					handleMutate={handleMutate}
					isOpen={isOpen}
				/>
			) : null}
		</DealVendorNoteContext.Provider>
	);
}
